/**
 * The match state machine (invariant 6).
 *
 * One reducer, two callers. The client folds the same events for optimistic UI
 * that the server folds as the authority, so divergence is impossible by
 * construction rather than merely tested for. A ghost replay is not a second
 * code path either — invariant 1 means a recorded stream is exactly what the
 * live socket would have produced, fed to this same reducer.
 *
 * Purity rules, strictly held:
 *   - no timers. The caller owns the clock and feeds `local.tick`.
 *   - no wall clock. Every deadline is expressed in SERVER time (invariant 2).
 *   - no I/O, no randomness, no mutation of the events it is handed.
 */

import type { Decision, LngLat, RoundOutcome, Tier } from '@geogame/shared';
import { TIER_CONFIG, WINDOW_RTT_CREDIT_CAP_MS, isCoachedRound, roundClockMs } from '@geogame/shared';

import type {
  MatchEvent,
  MatchMachine,
  MatchPhase,
  MatchMachineOptions,
  MatchPlan,
  MatchState,
  OpponentState,
  RoundState,
} from './types';

function initialState(plan: MatchPlan, tier: Tier): MatchState {
  return {
    phase: 'idle',
    tier,
    roundIdx: -1,
    roundCount: plan.rounds.length,
    bankedTotal: 0,
    round: null,
    opponent: null,
    finished: false,
  };
}

function armRound(index: number, startAtServerTs: number, golden: boolean): RoundState {
  return {
    index,
    rung: 1,
    phase: 'armed',
    pinpointAttempt: 0,
    golden,
    pot: 0,
    banked: false,
    outcome: null,
    pinLngLat: null,
    pinCca3: null,
    decision: null,
    rungStartedAtServerTs: null,
    // While armed, the "deadline" is the reveal instant: both clients reveal at
    // their local estimate of it, and input before it is discarded (spec 8.2).
    deadlineServerTs: startAtServerTs,
  };
}

/**
 * End the round. `credit` is the only path by which points reach the banked
 * total — invariant 9 in code form: a bust or a timeout closes the round with
 * pot 0 and leaves everything previously banked untouched.
 */
function closeRound(
  state: MatchState,
  round: RoundState,
  outcome: RoundOutcome,
  decision: Decision | null,
  credit: boolean,
): MatchState {
  const pot = credit ? round.pot : 0;
  return {
    ...state,
    phase: 'round-end',
    bankedTotal: state.bankedTotal + (credit ? pot : 0),
    round: {
      ...round,
      phase: 'round-end',
      pot,
      banked: credit,
      outcome,
      decision,
      deadlineServerTs: null,
    },
  };
}

export function createMatchMachine(
  plan: MatchPlan,
  tier: Tier,
  opts: MatchMachineOptions = {},
): MatchMachine {
  const cfg = TIER_CONFIG[tier];
  /*
   * PER ROUND, NOT PER MATCH, because the guided round runs on a slower clock
   * than the rounds after it — see `roundClockMs`. These used to be two
   * constants closed over at construction, which is what made a per-round
   * clock impossible and is the only reason they are functions now.
   *
   * The machine must agree with the practice driver to the millisecond: the
   * driver expires the rung and the machine draws the deadline the ring counts
   * down to, so a disagreement here looks exactly like the game cheating.
   * Both go through `roundClockMs`, so there is nothing to keep in sync.
   */
  const coached = opts.coached === true;
  const pace = opts.pace ?? 'standard';
  const rungMsFor = (roundIdx: number): number =>
    roundClockMs(tier, isCoachedRound(coached, roundIdx), pace).rungMs;
  const decisionMsFor = (roundIdx: number): number =>
    roundClockMs(tier, isCoachedRound(coached, roundIdx), pace).decisionMs;

  let state: MatchState = initialState(plan, tier);
  /**
   * Latest server instant the machine has been told about. Every deadline it
   * sets is derived from this, so the caller must feed `local.tick` — the
   * machine has no clock of its own and is not allowed to acquire one.
   */
  let now = 0;
  /** startAtServerTs from the live round's arm, kept for the reveal. */
  let armedStartAt: number | null = null;
  /*
   * Extra milliseconds rung 1 runs for, because its prompt is a sentence.
   *
   * Kept beside `armedStartAt` and for the same reason: the reveal can be
   * reached from three places, and only one of them carries anything about the
   * round. The arm frame is the one event all three are downstream of, so it
   * is where the value is captured.
   */
  let armedReadMs = 0;
  /** Rungs already answered this round; blocks a double submit. */
  let answered = new Set<number>();

  const isLive = (roundIdx: number): RoundState | null => {
    const r = state.round;
    if (!r || state.finished) return null;
    return r.index === roundIdx ? r : null;
  };

  /*
   * THE PINPOINT RUNG, and the only rung that gets reading time.
   *
   * Every other rung shows its prompt above four options that are themselves
   * the answer space: question and candidates are read together, in one place,
   * and the clock was sized for that. This one shows a sentence above a globe,
   * and the search cannot start until the sentence is finished.
   */
  const reveal = (round: RoundState, at: number): MatchState => ({
    ...state,
    phase: 'pinpoint',
    round: {
      ...round,
      phase: 'pinpoint',
      rungStartedAtServerTs: at,
      deadlineServerTs: at + rungMsFor(round.index) + armedReadMs,
    },
  });

  /** Fire whatever the elapsed clock has made due. One deadline per tick. */
  const applyDeadlines = (): MatchState => {
    const round = state.round;
    if (!round || state.finished) return state;
    const deadline = round.deadlineServerTs;
    if (deadline === null || now < deadline) return state;

    switch (round.phase) {
      case 'armed':
        // The key may still be in flight; both seats reveal on their own
        // estimate of startAtServerTs, which is the point of arming (spec 8.2).
        return reveal(round, armedStartAt ?? deadline);
      case 'pinpoint':
        // Answer timeout is a bust. Punish not knowing (spec 2).
        return closeRound(state, round, 'missed', 'timeout', false);
      case 'probe':
        return closeRound(state, round, 'timeout', 'timeout', false);
      case 'decision':
        // Decision timeout is an auto-bank. Don't punish hesitating on the
        // meta-choice (spec 2).
        return closeRound(state, round, 'banked', 'timeout', true);
      default:
        return state;
    }
  };

  const dispatch = (event: MatchEvent): MatchState => {
    switch (event.t) {
      // -- clock ------------------------------------------------------------
      case 'local.tick': {
        if (Number.isFinite(event.serverTs)) now = Math.max(now, event.serverTs);
        state = applyDeadlines();
        return state;
      }

      case 'welcome': {
        now = Math.max(now, event.serverTs);
        return state;
      }

      case 'matched': {
        now = Math.max(now, event.serverTs);
        // A `matched` on a resume must not flatten a live round back to idle
        // (spec 8.6): the room outlived the socket and the clock never paused.
        state = {
          ...state,
          roundCount: event.roundCount,
          phase: state.round === null ? 'idle' : state.phase,
        };
        return state;
      }

      /**
       * Resume (spec 8.6). The server is the authority on where this seat
       * actually is, so the snapshot is adopted wholesale rather than
       * reconstructed by replaying events the client may have missed.
       */
      case 'match.sync': {
        if (state.finished) return state;
        state = {
          ...state,
          bankedTotal: event.bankedTotal,
          roundIdx: event.round?.index ?? state.roundIdx,
          phase: event.round?.phase ?? state.phase,
          round:
            event.round === null
              ? null
              : {
                  index: event.round.index,
                  rung: event.round.rung,
                  phase: event.round.phase,
                  pot: event.round.pot,
                  banked: event.round.banked,
                  outcome: event.round.outcome,
                  pinLngLat: event.round.pinLngLat,
                  pinCca3: event.round.pinCca3,
                  decision: event.round.decision,
                  rungStartedAtServerTs: event.round.rungStartedAtServerTs,
                  deadlineServerTs: event.round.deadlineServerTs,
                  pinpointAttempt: event.round.pinpointAttempt ?? 0,
                  golden: event.round.golden ?? plan.rounds[event.round.index]?.golden === true,
                },
        };
        // Rungs this seat already answered before the drop must stay closed.
        if (event.round !== null) {
          answered = new Set<number>();
          for (let r = 1; r < event.round.rung; r += 1) answered.add(r);
          if (event.round.phase === 'decision') answered.add(event.round.rung);
        }
        return state;
      }

      // -- round lifecycle --------------------------------------------------
      case 'round.arm': {
        if (state.finished) return state;
        armedStartAt = event.startAtServerTs;
        armedReadMs = Math.max(0, event.readSeconds ?? 0) * 1000;
        answered = new Set<number>();
        state = {
          ...state,
          phase: 'armed',
          roundIdx: event.roundIdx,
          round: armRound(
            event.roundIdx,
            event.startAtServerTs,
            // The FRAME is the authority: the client's shell plan carries no
            // golden flag (it has no answer keys and no rounds to hang one on),
            // so the arm's in-the-clear marker is what lights the ladder up.
            event.golden === true || plan.rounds[event.roundIdx]?.golden === true,
          ),
        };
        return state;
      }

      case 'round.key': {
        const round = isLive(event.roundIdx);
        if (!round || round.phase !== 'armed') return state;
        state = reveal(round, armedStartAt ?? now);
        return state;
      }

      case 'local.reveal': {
        if (Number.isFinite(event.serverTs)) now = Math.max(now, event.serverTs);
        const round = isLive(event.roundIdx);
        if (!round || round.phase !== 'armed') return state;
        state = reveal(round, armedStartAt ?? event.serverTs);
        return state;
      }

      // -- player input -----------------------------------------------------
      case 'local.pinpoint': {
        const round = isLive(event.roundIdx);
        // Input before the reveal is discarded, not queued (spec 8.2).
        if (!round || round.phase !== 'pinpoint' || round.pinLngLat !== null) return state;
        state = {
          ...state,
          round: {
            ...round,
            pinLngLat: event.lngLat,
            pinCca3: event.cca3,
            pinpointAttempt: round.pinpointAttempt + 1,
          },
        };
        return state;
      }

      case 'local.answer': {
        const round = isLive(event.roundIdx);
        if (!round || round.phase !== 'probe') return state;
        if (round.rung !== event.rung || answered.has(event.rung)) return state;
        answered.add(event.rung);
        return state;
      }

      case 'local.push': {
        const round = isLive(event.roundIdx);
        if (!round || round.phase !== 'decision') return state;
        if (round.rung >= cfg.ladderDepth) return state;
        const rung = round.rung + 1;
        state = {
          ...state,
          phase: 'probe',
          round: {
            ...round,
            rung,
            phase: 'probe',
            decision: 'push',
            rungStartedAtServerTs: now,
            deadlineServerTs: now + rungMsFor(round.index),
          },
        };
        return state;
      }

      case 'local.bank': {
        const round = isLive(event.roundIdx);
        if (!round || round.phase !== 'decision') return state;
        state = closeRound(state, round, 'banked', 'bank', true);
        return state;
      }

      // -- authority --------------------------------------------------------
      case 'rung.result': {
        const round = isLive(event.roundIdx);
        if (!round || round.phase === 'round-end') return state;
        if (event.rung !== round.rung) return state;

        // CHANGE 4. A retry frame is NOT a resolution. The pinpoint was wrong
        // but the tier's allowance is not spent, so the rung stays in play, the
        // round stays open, and all that changes is the attempt count and the
        // pin the player is free to move. Treating this as a bust is exactly
        // the bug the `status` discriminant exists to make unwritable.
        if (event.status === 'retry') {
          // INVARIANT 2. The retry cost this player one round trip they could
          // not avoid — the verdict had to fly home before they could choose
          // where to tap next — so the window moves out by exactly that much.
          // Applied HERE rather than in the room so that client and server
          // fold the identical frame into the identical deadline (invariant 6);
          // a credit applied on only one side is a `round_closed` for an answer
          // the other side thought was in time. Capped, because window is what
          // a stalled pong would be buying.
          const credit = Math.max(
            0,
            Math.min(event.deadlineCreditMs ?? 0, WINDOW_RTT_CREDIT_CAP_MS),
          );
          const deadline = round.deadlineServerTs;
          state = {
            ...state,
            round: {
              ...round,
              pinpointAttempt: event.attempts.attempt,
              pinLngLat: null,
              pinCca3: null,
              deadlineServerTs: deadline === null ? null : deadline + credit,
            },
          };
          return state;
        }

        if (!event.correct) {
          // Invariant 9: the current round's pot is lost, nothing else is.
          const busted = { ...round, pot: 0 };
          state = closeRound(state, busted, round.rung === 1 ? 'missed' : 'busted', 'bust', false);
          return state;
        }

        const cleared: RoundState = { ...round, pot: event.pot };
        if (!event.canPush) {
          // Ladder depth reached — there is no decision left to make.
          state = closeRound(state, cleared, 'banked', null, true);
          return state;
        }

        state = {
          ...state,
          phase: 'decision',
          round: {
            ...cleared,
            phase: 'decision',
            decision: null,
            deadlineServerTs: now + decisionMsFor(cleared.index),
          },
        };
        return state;
      }

      case 'round.end': {
        const round = state.round;
        if (!round || round.index !== event.roundIdx) return state;
        const you = event.you;
        state = {
          ...state,
          phase: 'round-end',
          bankedTotal: you.total,
          round: {
            ...round,
            phase: 'round-end',
            rung: you.finalRung,
            pot: you.pot,
            banked: you.banked,
            outcome: you.outcome,
            pinLngLat: you.pinLngLat ?? round.pinLngLat,
            deadlineServerTs: null,
          },
          opponent: {
            // `round.end` carries numbers, not names. The rival's identity was
            // established by `opp.state` and outlives this frame — dropping it
            // here would blank the ladder's title at the moment the round is
            // most worth reading.
            playerId: state.opponent?.playerId ?? '',
            handle: state.opponent?.handle ?? '',
            roundIdx: event.roundIdx,
            rung: event.opp.finalRung,
            banked: event.opp.banked,
            total: event.opp.total,
            lngLat: event.opp.pinLngLat,
            fieldHighRung: Math.max(
              state.opponent !== null && state.opponent.roundIdx === event.roundIdx
                ? state.opponent.fieldHighRung
                : 0,
              event.opp.finalRung,
            ),
          },
        };
        return state;
      }

      case 'match.end': {
        state = { ...state, phase: 'match-end', finished: true };
        return state;
      }

      // -- opponent mirroring ------------------------------------------------
      case 'opp.state': {
        /**
         * The gate. An opponent's pin coordinate is the answer to rung 1, so it
         * is stored only once THIS seat has locked its own pinpoint for the
         * same round. The server is supposed to withhold it; the machine
         * refuses it anyway, because a leak that only one layer prevents is a
         * leak waiting for a refactor.
         */
        const round = state.round;
        const lockedOwnPin =
          round !== null && round.index === event.roundIdx && round.pinLngLat !== null;
        const lngLat: LngLat | null = lockedOwnPin && event.lngLat ? event.lngLat : null;

        // Clamped rather than assigned. The server already emits a high-water
        // mark, but a resync replays frames and a socket can deliver them out
        // of order; taking the max means a late-arriving stale frame cannot
        // walk the field marker backwards on screen.
        const prevOpp = state.opponent;
        const sameRound = prevOpp !== null && prevOpp.roundIdx === event.roundIdx;
        const fieldHighRung = Math.max(
          event.fieldHighRung,
          sameRound ? prevOpp.fieldHighRung : 0,
        );

        const opponent: OpponentState = {
          playerId: event.playerId,
          handle: event.handle,
          roundIdx: event.roundIdx,
          rung: event.rung,
          banked: event.banked,
          total: event.total,
          lngLat,
          fieldHighRung,
        };
        state = { ...state, opponent };
        return state;
      }

      case 'error': {
        if (event.fatal) state = { ...state, phase: 'match-end', finished: true };
        return state;
      }

      // Nothing here moves match state.
      case 'pong':
      case 'queued':
        return state;

      default:
        return state;
    }
  };

  const remainingMs = (s: MatchState, nowServerTs: number): number => {
    const deadline = s.round?.deadlineServerTs;
    if (deadline === null || deadline === undefined) return 0;
    return Math.max(0, deadline - nowServerTs);
  };

  const machine: MatchMachine = {
    plan,
    tier,
    getState: () => state,
    dispatch,
    reset: () => {
      state = initialState(plan, tier);
      now = 0;
      armedStartAt = null;
      answered = new Set<number>();
      return state;
    },
    selectors: {
      currentRung: (s) => s.round?.rung ?? 0,
      pot: (s) => s.round?.pot ?? 0,
      bankedTotal: (s) => s.bankedTotal,
      remainingMs,
      phase: (s): MatchPhase => s.phase,
      canPush: (s) => s.phase === 'decision' && (s.round?.rung ?? 0) < cfg.ladderDepth,
      canBank: (s) => s.phase === 'decision' && s.round !== null,
    },
  };

  return machine;
}
